// Registro do service worker e sinal de "nova versão pronta". Sem React, para
// poder ser importado tanto pelo main quanto pela tela de atualização sem ciclo.
//
// Este módulo SUBSTITUI o antigo detector por hash do index.html: com o SW no ar,
// um fetch("/index.html") é respondido pelo precache e a comparação de hash
// devolveria "sem novidade" para sempre.

// `registerSW` só existe no build (módulo virtual do vite-plugin-pwa). Em teste
// e em dev o import dinâmico falha e o registro simplesmente não acontece.
let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;
let registration: { update: () => Promise<unknown> } | null = null;

let updateReady = false;
const listeners = new Set<() => void>();

// Assina o sinal. Se a versão nova JÁ estava pronta, avisa na hora — o SW pode
// ficar pronto antes do React montar, e um sinal perdido só apareceria no
// próximo deploy.
export function onUpdateReady(listener: () => void): () => void {
  listeners.add(listener);
  if (updateReady) listener();
  return () => listeners.delete(listener);
}

// Idempotente: dois sinais para a mesma versão não devem virar dois avisos.
export function notifyUpdateReady(): void {
  if (updateReady) return;
  updateReady = true;
  for (const listener of listeners) listener();
}

// Só para teste: zera o estado de módulo entre casos.
export function resetUpdateListeners(): void {
  updateReady = false;
  listeners.clear();
  updateSW = null;
  registration = null;
  stopUpdateChecks?.();
  stopUpdateChecks = null;
}

// Só para teste: injeta o `updateSW` que em produção vem do módulo virtual.
export function setUpdateSWForTest(fn: ((reloadPage?: boolean) => Promise<void>) | null): void {
  updateSW = fn;
}

export function hasServiceWorkerControl(): boolean {
  return typeof navigator !== "undefined" && Boolean(navigator.serviceWorker?.controller);
}

export function checkForSwUpdate(): void {
  void registration?.update().catch(() => {});
}

// Quanto esperar o SW novo assumir a aba antes de recarregar assim mesmo.
export const CONTROLLER_WAIT_MS = 3000;

// Espera o SW novo assumir o controle da aba (`controllerchange`), com teto de
// tempo. Resolve mesmo no estouro: o reload não pode ficar refém de um evento
// que talvez nunca chegue.
function waitForControllerChange(timeoutMs: number): Promise<void> {
  if (!("serviceWorker" in navigator)) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener("controllerchange", done);
      resolve();
    };
    const timer = setTimeout(done, timeoutMs);
    navigator.serviceWorker.addEventListener("controllerchange", done);
  });
}

// Ativa o SW novo e recarrega. Se o registro não aconteceu (dev, browser sem
// suporte), o reload puro já basta: o index.html é servido com no-store.
//
// O reload é NOSSO de propósito. O `updateSW(reloadPage)` do vite-plugin-pwa
// ignora o argumento: ele só manda SKIP_WAITING e delega o reload a um listener
// de `controlling` guardado por `event.isUpdate` — e `isUpdate` é
// `Boolean(navigator.serviceWorker.controller)` no instante do register, ou seja
// FALSO numa aba que nunca teve controller (primeira visita, já que o SW gerado
// não chama `clients.claim()`). Nessa aba o `await updateSW(true)` resolvia
// normalmente e o reload nunca vinha: a tela "Atualizando…" ficava presa.
export async function applyUpdate(): Promise<void> {
  if (!updateSW) {
    location.reload();
    return;
  }
  await updateSW(true);
  // Recarregar já com o SW novo no comando faz a versão nova vir do precache
  // dele; se ele não assumir a tempo, o reload busca da rede e funciona igual.
  await waitForControllerChange(CONTROLLER_WAIT_MS);
  location.reload();
}

// De quanto em quanto tempo perguntar ao servidor se saiu build nova. O browser
// só rechecha o sw.js numa navegação de verdade, que numa SPA quase não
// acontece: sem isto, uma aba aberta desde de manhã nunca fica sabendo do deploy.
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

// Agenda a checagem de build nova: periódica e a cada volta da aba ao foco (o
// vendedor volta do WhatsApp e já pega a versão nova). Devolve a função que
// cancela as duas — sem ela, um registro novo deixaria o intervalo antigo vivo.
export function schedulePeriodicUpdateCheck(
  registration: { update: () => Promise<unknown> },
  intervalMs: number = UPDATE_CHECK_INTERVAL_MS,
): () => void {
  const check = () => {
    // Offline ou servidor fora do ar rejeitam; nada a fazer além de tentar de
    // novo no próximo ciclo.
    void registration.update().catch(() => {});
  };
  const timer = setInterval(check, intervalMs);
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") check();
  };
  document.addEventListener("visibilitychange", onVisibilityChange);
  return () => {
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}

let stopUpdateChecks: (() => void) | null = null;

export function registerAppServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (!("serviceWorker" in navigator)) return;
  void import("virtual:pwa-register")
    .then(({ registerSW }) => {
      updateSW = registerSW({
        onNeedRefresh: notifyUpdateReady,
        // O reload passa a ser SEMPRE decisão nossa. Em modo "prompt" o
        // vite-plugin-pwa instala um listener de `controlling` que chama
        // `window.location.reload()` sozinho quando `onNeedReload` não é
        // fornecido (ver node_modules/vite-plugin-pwa/dist/client/build/register.js).
        // Como o `skipWaiting` ativa o SW novo para TODOS os clientes da
        // registration, uma segunda aba que aplica a atualização faria a aba em
        // ligação receber `controlling` e recarregar por conta própria — a
        // guarda por rota do AppUpdateWatcher não veria nada. Aqui só
        // registramos o sinal (idempotente); quem recarrega é o applyUpdate.
        onNeedReload: notifyUpdateReady,
        onRegisteredSW: (_swUrl, registration) => {
          if (!registration) return;
          setRegistration(registration);
          stopUpdateChecks?.();
          stopUpdateChecks = schedulePeriodicUpdateCheck(registration);
        },
      });
    })
    .catch(() => {
      /* sem SW: o app funciona igual, só perde a abertura instantânea */
    });
}

function setRegistration(value: { update: () => Promise<unknown> }): void {
  registration = value;
}
